import { create } from 'zustand';

import { EMagicPenColor, EMagicPenShape, ICanvasObject, TSaveModalTriggerSource, TToolType } from '@src/types';

interface ISaveAndShareModalStates {
  isSaveModalOpen: boolean;
  saveModalTriggerSource: TSaveModalTriggerSource;
}

interface IMagicPenStore extends ISaveAndShareModalStates {
  // Tool states
  activeTool: TToolType;
  setActiveTool: (tool: TToolType) => void;

  lastSelectedToolIcon: React.ReactNode | null;
  setLastSelectedToolIcon: (icon: React.ReactNode | null) => void;

  // Shape states (separate from tool states)
  activeShape: EMagicPenShape | null;
  setActiveShape: (shape: EMagicPenShape | null) => void;

  // Brush states
  brushColor: EMagicPenColor;
  setBrushColor: (color: EMagicPenColor) => void;
  /** Increments on every color selection to trigger updates even when selecting same color */
  colorUpdateTrigger: number;

  // Canvas drawing states
  currentLinePoints: number[];
  setCurrentLinePoints: (line: number[]) => void;

  lineStartPoint: { x: number; y: number } | null;
  setLineStartPoint: (point: { x: number; y: number } | null) => void;

  eraserStrokes?: Array<{ id: string; points: number[]; createdAt: number }>;
  setEraserStrokes: (eraserStrokes: Array<{ id: string; points: number[]; createdAt: number }>) => void;

  selectedId: string | null;
  setSelectedId: (id: string | null) => void;

  hoveredId: string | null;
  setHoveredId: (id: string | null) => void;

  shapeStartPoint: { x: number; y: number } | null;
  setShapeStartPoint: (point: { x: number; y: number } | null) => void;

  currentShape: ICanvasObject | null;
  setCurrentShape: (shape: ICanvasObject | null) => void;

  activeSticker: string | undefined;
  setActiveSticker: (sticker: string | undefined) => void;

  // Canvas objects
  objects: ICanvasObject[];
  setObjects: (objects: ICanvasObject[]) => void;
  addObjects: (objects: ICanvasObject[], callback?: (objects: ICanvasObject[]) => void) => void;
  clearObjects: () => void;
  bringToFront: (id: string, onUpdateCallback?: (obj: ICanvasObject[]) => void) => void;

  // Z-index counter for managing object layering
  maxZIndex: number;
  setMaxZIndex: (zIndex: number) => void;

  // Save modal states
  isSaveModalOpen: boolean;
  setSaveModalState: ({ isSaveModalOpen, saveModalTriggerSource }: Partial<ISaveAndShareModalStates>) => void;
  saveModalTriggerSource: TSaveModalTriggerSource;

  // Utility functions
  resetStates: () => void;
  resetAllStates: () => void;

  // Text selection state
  selectedTextRange: { start: number; end: number } | null;
  setSelectedTextRange: (range: { start: number; end: number } | null) => void;

  // Text color change mode - when true, color changes apply to text, not background
  isTextColorChangeMode: boolean;
  setIsTextColorChangeMode: (mode: boolean) => void;

  // Sticky note functions
  updateStickyNoteContent: (id: string, content: string, options?: { hasSynced?: boolean }) => void;
  updateStickyNoteColor: (id: string, backgroundColor: EMagicPenColor, textColor?: string) => void;
  updateStickyNoteTextColor: (id: string, textColor: string, start?: number, end?: number) => void;

  annotatorIds: string[];
  setAnnotatorIds: (annotatorIds: string[]) => void;
}

export const useMagicPenStore = create<IMagicPenStore>((set) => ({
  // Initial states
  activeTool: 'select',
  lastSelectedToolIcon: null,
  activeShape: null,
  brushColor: EMagicPenColor.RED,
  colorUpdateTrigger: 0,
  currentLinePoints: [],
  lineStartPoint: null,
  selectedId: null,
  hoveredId: null,
  shapeStartPoint: null,
  currentShape: null,
  activeSticker: undefined,
  objects: [],
  eraserStrokes: [],
  selectedTextRange: null,
  isTextColorChangeMode: false,
  isSaveModalOpen: false,
  saveModalTriggerSource: 'toolbar',
  maxZIndex: 0, // Initialize maxZIndex to 0
  annotatorIds: [],

  // Setters
  setActiveTool: (activeTool) => set(() => ({ activeTool })),
  setLastSelectedToolIcon: (lastSelectedToolIcon) => set(() => ({ lastSelectedToolIcon })),
  setBrushColor: (brushColor) => set((state) => ({ brushColor, colorUpdateTrigger: state.colorUpdateTrigger + 1 })),
  setCurrentLinePoints: (currentLinePoints) => set(() => ({ currentLinePoints })),

  setActiveShape: (activeShape) => set(() => ({ activeShape })),
  setLineStartPoint: (lineStartPoint) => set(() => ({ lineStartPoint })),
  setSelectedId: (selectedId) => set(() => ({ selectedId })),
  setHoveredId: (hoveredId) => set(() => ({ hoveredId })),
  setShapeStartPoint: (shapeStartPoint) => set(() => ({ shapeStartPoint })),
  setCurrentShape: (currentShape) => set(() => ({ currentShape })),
  setActiveSticker: (activeSticker) => set(() => ({ activeSticker })),
  setObjects: (objects) =>
    set((state) => {
      // Calculate the new maxZIndex from the objects, but also handle objects that don't have z-index
      const maxZIndex = objects.reduce((max, obj) => Math.max(max, obj.zIndex || 0), 0);

      const selectedExists = state.selectedId ? objects.some((obj) => obj.id === state.selectedId) : true;
      const hoveredExists = state.hoveredId ? objects.some((obj) => obj.id === state.hoveredId) : true;

      return {
        objects,
        maxZIndex, // Update maxZIndex to the maximum found in objects
        selectedId: selectedExists ? state.selectedId : null,
        hoveredId: hoveredExists ? state.hoveredId : null,
      };
    }),
  addObjects: (newObjects, callback) =>
    set((state) => {
      // Assign z-index to new objects and update maxZIndex
      let newMaxZIndex = state.maxZIndex;
      const objectsWithZIndex = newObjects.map((obj) => {
        // If the object already has a z-index, use it, otherwise assign a new one
        if (obj.zIndex !== undefined) {
          // Update newMaxZIndex if this object has a higher z-index
          newMaxZIndex = Math.max(newMaxZIndex, obj.zIndex);
        } else {
          // Assign a new z-index
          newMaxZIndex++;
        }
        return { ...obj, zIndex: newMaxZIndex };
      });

      // Call the callback with the objects that have been assigned z-index values
      if (callback) {
        callback(objectsWithZIndex);
      }

      return {
        objects: [...state.objects, ...objectsWithZIndex],
        maxZIndex: newMaxZIndex, // Update maxZIndex
      };
    }),
  clearObjects: () => set(() => ({ objects: [], maxZIndex: 0 })), // Reset maxZIndex when clearing objects
  bringToFront: (id, onUpdateCallback) =>
    set((state) => {
      const objIndex = state.objects.findIndex((obj) => obj.id === id);
      if (objIndex === -1) return state;

      // Increment maxZIndex and assign it to the object
      const newMaxZIndex = state.maxZIndex + 1;
      const updatedObjects = [...state.objects];
      const updatedObj = { ...updatedObjects[objIndex], zIndex: newMaxZIndex };
      updatedObjects[objIndex] = updatedObj;

      // If callback is provided, call it with the updated object
      if (onUpdateCallback) {
        onUpdateCallback([updatedObj]);
      }

      return {
        objects: updatedObjects,
        maxZIndex: newMaxZIndex, // Update maxZIndex
      };
    }),
  setMaxZIndex: (zIndex) => set(() => ({ maxZIndex: zIndex })), // Setter for maxZIndex
  setEraserStrokes: (eraserStrokes) => set(() => ({ eraserStrokes })), // Setter for eraserStrokes

  setSelectedTextRange: (selectedTextRange) => set(() => ({ selectedTextRange })),
  setIsTextColorChangeMode: (isTextColorChangeMode) => set(() => ({ isTextColorChangeMode })),
  setSaveModalState: ({ isSaveModalOpen, saveModalTriggerSource }) =>
    set(() => ({ isSaveModalOpen, saveModalTriggerSource })),

  // Utility functions
  resetStates: () =>
    set((state) => ({
      lineStartPoint: null,
      currentLinePoints: [],
      shapeStartPoint: null,
      currentShape: null,
      activeSticker: undefined,
      selectedId: null,
      hoveredId: null,
      activeShape: null,
      // Keep the current maxZIndex to maintain proper layering across tool changes
      maxZIndex: state.maxZIndex,
    })),

  // Utility function to completely reset states including zIndex
  resetAllStates: () =>
    set(() => ({
      lineStartPoint: null,
      currentLinePoints: [],
      shapeStartPoint: null,
      currentShape: null,
      activeSticker: undefined,
      selectedId: null,
      hoveredId: null,
      activeShape: null,
      maxZIndex: 0, // Reset maxZIndex when completely resetting states
    })),

  // Sticky note functions
  updateStickyNoteContent: (id: string, content: string, options?: { hasSynced?: boolean }) =>
    set((state) => ({
      objects: state.objects.map((obj) =>
        obj.id === id
          ? {
              ...obj,
              content,
              isEditing: false,
              ...(options?.hasSynced !== undefined ? { hasSynced: options.hasSynced } : {}),
            }
          : obj
      ),
    })),

  updateStickyNoteColor: (id: string, backgroundColor: EMagicPenColor, textColor?: string) =>
    set((state) => ({
      objects: state.objects.map((obj) =>
        obj.id === id ? { ...obj, backgroundColor, textColor: textColor || obj.textColor } : obj
      ),
      brushColor: backgroundColor,
      colorUpdateTrigger: state.colorUpdateTrigger + 1,
    })),

  updateStickyNoteTextColor: (id: string, textColor: string, start?: number, end?: number) =>
    set((state) => ({
      objects: state.objects.map((obj) => {
        if (obj.id === id && obj.type === 'stickyNote') {
          // If start and end are provided, we need to handle partial text coloring
          // For now, we'll update the entire text color
          return { ...obj, textColor };
        }
        return obj;
      }),
    })),

  setAnnotatorIds: (annotatorIds) => set(() => ({ annotatorIds })),
}));
